import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, CalendarClock, Contact, TrendingUp, BadgeCheck,
  Wallet, Megaphone, Inbox, Settings, Plus, RotateCcw, Menu, X, UserPlus,
  PhoneCall, Bot, ChevronDown, PanelLeftClose, PanelLeftOpen, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/App";
import BrandMark from "@/components/BrandMark";
import CreateEventPicker from "@/components/CreateEventPicker";
import AddCustomerDialog from "@/components/host/AddCustomerDialog";

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

// Grouped navigation. Each item: { label, path, icon, exact? }
const NAV = [
  {
    heading: null,
    items: [
      { label: "Dashboard", path: "/host", icon: LayoutDashboard, exact: true },
      { label: "Offerings", path: "/host/events", icon: Calendar },
      { label: "Schedule", path: "/host/schedule", icon: CalendarClock },
      { label: "Customers", path: "/host/customers", icon: Contact },
    ],
  },
  {
    heading: "AI Agents",
    items: [
      { label: "Overview", path: "/host/ai", icon: Sparkles, exact: true },
      { label: "Voice Agent", path: "/host/ai/voice", icon: PhoneCall },
      { label: "Internal ChatGPT", path: "/host/ai/assistant", icon: Bot },
    ],
  },
  {
    heading: "Grow",
    items: [
      { label: "Marketing", path: "/host/marketing", icon: Megaphone },
      { label: "Inbox", path: "/host/inbox", icon: Inbox },
    ],
  },
  {
    heading: "Revenue",
    items: [
      { label: "Overview", path: "/host/revenue", icon: TrendingUp },
      { label: "Memberships", path: "/host/memberships", icon: BadgeCheck },
      { label: "Billing", path: "/host/billing", icon: Wallet },
    ],
  },
  {
    heading: null,
    items: [{ label: "Settings", path: "/host/settings", icon: Settings }],
  },
];

const isActive = (pathname, item) =>
  item.exact ? pathname === item.path : pathname === item.path || pathname.startsWith(item.path + "/");

const SidebarInner = ({ onNavigate, onCreate, onAddCustomer, collapsed = false, onToggleCollapse, openGroups = {}, onToggleGroup }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const go = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  const NavItem = ({ item }) => {
    const active = isActive(location.pathname, item);
    const Icon = item.icon;
    return (
      <button
        onClick={() => go(item.path)}
        title={collapsed ? item.label : undefined}
        className={`w-full flex items-center rounded-lg text-sm font-medium transition-colors ${
          collapsed ? "justify-center py-2.5" : "gap-3 px-3 py-2"
        } ${active ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"}`}
        data-testid={`host-nav-${slug(item.label)}`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        {!collapsed && item.label}
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Brand + collapse toggle */}
      <div className={`h-16 flex items-center border-b border-border shrink-0 ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}>
        {!collapsed && (
          <button onClick={() => go("/host")} className="focus:outline-none" data-testid="host-sidebar-brand">
            <BrandMark size="md" />
          </button>
        )}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            className="p-2 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="host-sidebar-collapse-toggle"
          >
            {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
        )}
      </div>

      {/* Quick add — register a walk-in from anywhere */}
      <div className={`pt-4 ${collapsed ? "px-2" : "px-3"}`}>
        <Button
          onClick={onAddCustomer}
          title={collapsed ? "Add customer" : undefined}
          className={`w-full bg-primary text-primary-foreground hover:bg-primary/90 ${collapsed ? "px-0" : "gap-1.5"}`}
          data-testid="host-sidebar-add-customer"
        >
          <UserPlus className="w-4 h-4" /> {!collapsed && "Add customer"}
        </Button>
      </div>

      {/* Nav */}
      <nav className={`flex-1 overflow-y-auto py-4 ${collapsed ? "px-2 space-y-1" : "px-3 space-y-4"}`} data-testid="host-sidebar-nav">
        {NAV.map((group, gi) => {
          if (!group.heading) {
            return (
              <div key={gi} className="space-y-0.5">
                {group.items.map((item) => <NavItem key={item.path} item={item} />)}
              </div>
            );
          }
          const open = openGroups[group.heading] !== false; // default open
          return (
            <div key={gi}>
              {collapsed ? (
                <div className="mx-2 mb-1 border-t border-border" />
              ) : (
                <button
                  onClick={() => onToggleGroup && onToggleGroup(group.heading)}
                  className="w-full flex items-center justify-between px-3 mb-1.5 rounded hover:bg-muted/40 py-0.5 transition-colors"
                  data-testid={`host-nav-group-${slug(group.heading)}`}
                >
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{group.heading}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
                </button>
              )}
              {(collapsed || open) && (
                <div className="space-y-0.5">
                  {group.items.map((item) => <NavItem key={item.path} item={item} />)}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className={`border-t border-border shrink-0 space-y-2 ${collapsed ? "p-2" : "p-3"}`}>
        <Button
          onClick={onCreate}
          title={collapsed ? "Create event" : undefined}
          className={`w-full bg-primary text-primary-foreground hover:bg-primary/90 ${collapsed ? "px-0" : "gap-1.5"}`}
          data-testid="host-sidebar-create"
        >
          <Plus className="w-4 h-4" /> {!collapsed && "Create event"}
        </Button>
        <button
          onClick={() => go("/")}
          title={collapsed ? "Switch to player view" : undefined}
          className={`w-full flex items-center rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors ${collapsed ? "justify-center py-2" : "gap-2 px-3 py-2"}`}
          data-testid="host-sidebar-switch-player"
        >
          <RotateCcw className="w-3.5 h-3.5" /> {!collapsed && "Switch to player view"}
        </button>
        <button
          onClick={() => go("/profile")}
          title={collapsed ? (user?.name || "Profile") : undefined}
          className={`w-full flex items-center rounded-lg hover:bg-muted/60 transition-colors ${collapsed ? "justify-center py-1.5" : "gap-2.5 px-2 py-1.5"}`}
          data-testid="host-sidebar-profile"
        >
          <Avatar className="w-8 h-8 ring-1 ring-border">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-primary/20 text-primary-ink text-xs">
              {getInitials(user?.name)}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="min-w-0 text-left">
              <p className="text-sm font-medium truncate leading-tight">{user?.name || "You"}</p>
              <p className="text-[11px] text-muted-foreground truncate leading-tight">{user?.email}</p>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

/**
 * Soft gate: a host without an active subscription still reaches the Hub but
 * sees this banner nudging them to subscribe. Hidden for non-hosts (e.g. the
 * platform admin) and for active subscribers.
 */
const SubscribeBanner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!user || !user.is_host) return null;
  if (user.subscription_status === "active" || user.subscription_status === "trialing") return null;
  return (
    <div className="bg-zinc-900 text-white px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2" data-testid="subscribe-banner">
      <div className="flex items-center gap-2 text-sm">
        <Sparkles className="w-4 h-4 text-[#d4ff4f] shrink-0" />
        <span>You're in limited mode. Subscribe to unlock everything in your Host Hub.</span>
      </div>
      <Button
        size="sm"
        onClick={() => navigate("/onboarding?step=plan")}
        className="rounded-full bg-[#d4ff4f] text-zinc-900 hover:bg-[#c3ef3a] shrink-0"
        data-testid="subscribe-banner-cta"
      >
        Subscribe now
      </Button>
    </div>
  );
};

/**
 * OS-style shell for every /host route: fixed left rail on desktop, slide-over
 * drawer on mobile. The desktop rail collapses to an icon-only strip, and each
 * headed nav group folds independently. Preferences persist in localStorage.
 */
const HostLayout = ({ children, fluid = false }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("host_sidebar_collapsed") === "1");
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("host_sidebar_groups")) || {}; } catch { return {}; }
  });

  const toggleCollapse = () => setCollapsed((c) => {
    const n = !c;
    localStorage.setItem("host_sidebar_collapsed", n ? "1" : "0");
    return n;
  });
  const toggleGroup = (heading) => setOpenGroups((prev) => {
    const next = { ...prev, [heading]: prev[heading] === false ? true : false };
    localStorage.setItem("host_sidebar_groups", JSON.stringify(next));
    return next;
  });

  const openAddCustomer = () => {
    setDrawerOpen(false);
    setAddCustomerOpen(true);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="host-layout">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex fixed inset-y-0 left-0 ${collapsed ? "w-16" : "w-60"} bg-card border-r border-border z-40 flex-col transition-[width] duration-200`}>
        <SidebarInner
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          openGroups={openGroups}
          onToggleGroup={toggleGroup}
          onCreate={() => setPickerOpen(true)}
          onAddCustomer={openAddCustomer}
        />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-card border-b border-border h-14 flex items-center justify-between px-4">
        <button
          onClick={() => setDrawerOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-muted/60"
          aria-label="Open menu"
          data-testid="host-mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
        <BrandMark size="sm" />
        <button
          onClick={() => setPickerOpen(true)}
          className="p-2 -mr-2 rounded-lg hover:bg-muted/60 text-primary-ink"
          aria-label="Create event"
          data-testid="host-mobile-create-btn"
        >
          <Plus className="w-5 h-5" />
        </button>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="host-mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-card border-r border-border shadow-2xl animate-in slide-in-from-left duration-200">
            <button
              onClick={() => setDrawerOpen(false)}
              className="absolute top-4 right-3 p-2 rounded-lg hover:bg-muted/60 z-10"
              aria-label="Close menu"
              data-testid="host-drawer-close"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarInner
              openGroups={openGroups}
              onToggleGroup={toggleGroup}
              onNavigate={() => setDrawerOpen(false)}
              onCreate={() => {
                setDrawerOpen(false);
                setPickerOpen(true);
              }}
              onAddCustomer={openAddCustomer}
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className={collapsed ? "lg:pl-16" : "lg:pl-60"}>
        <SubscribeBanner />
        <main className={fluid ? "px-4 sm:px-6 py-6 sm:py-8" : "max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8"}>{children}</main>
      </div>

      <CreateEventPicker open={pickerOpen} onOpenChange={setPickerOpen} fromHost />
      <AddCustomerDialog open={addCustomerOpen} onOpenChange={setAddCustomerOpen} />
    </div>
  );
};

export default HostLayout;
